import React from 'react';
import { Form } from 'react-final-form';
import { useSelector } from 'react-redux';
import { Url } from 'src/api';
import CardContainer from 'src/components/common/CardContainer.redesign';

import Button from 'src/components/controls/forms/redesign/FormButton';
import SurfaceTract from 'src/models/SurfaceTract';
import { IRootState } from 'src/reducers';
import useFetch from 'use-http';
import { DialogContext, dialogCreators } from 'src/providers/DialogProvider';
import SurfaceTractCard from './SurfaceTractCard';
import { AnyObject, FormApi } from 'final-form';
import arrayMutators from 'final-form-arrays';
import { FieldArray } from 'react-final-form-arrays';
import _ from 'lodash';
import { LandSystem } from 'src/models/LandSystem';
import OwnershipContactCategory from 'src/models/OwnershipContactCategory';
import { SelectItem } from 'src/models/common/SelectItem';
import Ownership from 'src/models/tract_detail/Ownership';
import moment from 'moment';
import County from 'src/models/County';
import PagedResultSet from 'src/models/common/PagedResultSet';
import ProjectStaff from 'src/models/ProjectStaff';

interface ISurfaceOwnershipListProps {
    landSystem: LandSystem;
    isFormDirty?: boolean;
    ownershipContactCategories: Array<OwnershipContactCategory>;
    counties: Array<County>;
    projectStaff: PagedResultSet<ProjectStaff>;
    openContactNotes: (ownership: Ownership) => void;
    updateTractPage: () => void;
}

const SurfaceOwnershipList = React.forwardRef<{}, ISurfaceOwnershipListProps>((props, ref) => {
    let formApi: FormApi = null;
    let submit: (e?: React.SyntheticEvent<HTMLFormElement, Event>) => Promise<AnyObject> = null;

    React.useImperativeHandle(ref, () => ({
        // saveOwnerships
        saveForm: () => {
            submit();
        },
    }));

    const appContext = useSelector((state: IRootState) => state.appContext);
    const [loading, setLoading] = React.useState(true);
    const { get, post, response } = useFetch();
    const dialogContext = React.useContext<dialogCreators>(DialogContext);

    const [surfaceTracts, setSurfaceTracts] = React.useState<Array<SurfaceTract>>([]);
    const [leaseBuyerList, setLeaseBuyerList] = React.useState<SelectItem[]>([]);

    React.useEffect(() => {
        if (!!props.projectStaff) {
            setLeaseBuyerList(props.projectStaff && props.projectStaff.items.length > 0
                ? props.projectStaff.items
                      .map((landman: AnyObject) => new SelectItem(landman.id, landman.user.firstName + ' ' + landman.user.lastName))
                : []
            );
        }
    }, [props]);

    React.useEffect(() => {
        // https://github.com/final-form/final-form/issues/142
        setTimeout(formApi?.reset);
    }, [loading]);

    const warningStyle: React.CSSProperties = {
        display: 'inline-block',
        padding: '0.5rem',
        margin: '0 0 1rem 0',
        fontWeight: 700,
        background: '#d5d5d5',
        fontSize: '.8em',
        color: '#565656',
        width: '100%',
        textAlign: 'center',
        borderRadius: '0.4rem',
    };

    const loadSurfaceOwnerships = async () => {
        setLoading(true);
        let surfaceTractCallUrl = Url.tract.getTract(appContext) + '/surfaceTracts';
        console.log(surfaceTractCallUrl);
        let data = await get(surfaceTractCallUrl);

        if (data && data.length > 0) {
            data = data.map((sp: SurfaceTract) => {
                if (sp.recordTypeId) {
                    sp.recordDate = (moment(sp.recordDate).format('YYYY-MM-DD') as unknown as Date);
                }
                return sp;
            });
        }

        if (response.ok) {
            setSurfaceTracts(data);
            setLoading(false);
        }
    };

    const formatOwnership = (ownership: Ownership) => {
        ownership.decimalInterest = parseFloat(ownership.decimalInterest + '');
        if (isNaN(ownership.decimalInterest)) {
            delete ownership.decimalInterest;
        }
        ownership.additionalOwnerships?.map((ao: Ownership) => {
            ao = formatOwnership(ao);
        });
        return ownership;
    };

    const onSubmit = async (values: { surfaceTracts: Array<SurfaceTract> }) => {
        const list = values.surfaceTracts.map((so: SurfaceTract) => {
            so.acres = parseFloat(so.acres + '');

            if (isNaN(so.acres)) {
                delete so.acres;
            }

            if (!so.isPartial) {
                so.partialAcreage = 0;
            }

            if (so.ownerships && so.ownerships.length > 0) {
                so.ownerships.map((own: Ownership) => {
                    own = formatOwnership(own);
                });
            }

            return so;
        });

        await post(Url.tract.getTract(appContext) + '/surfaceTracts/update-batch', list);

        if (response.ok) {
            loadSurfaceOwnerships();
        }
    };

    React.useEffect(() => {
        loadSurfaceOwnerships();
    }, []);

    const addSurfaceTract = async (push: (values: AnyObject) => void, surfaceTract: SurfaceTract) => {
        let result = await post(`${Url.tract.getTract(appContext)}/surfaceTracts/attach`, surfaceTract);

        if (response.ok) {
            surfaceTract.number = result;
            push(surfaceTract);
        }
    };

    const createSurfaceTract = async (push: (values: AnyObject) => void) => {
        dialogContext.tract.createTract(
            props.landSystem, 
            appContext.fieldNumber, 
            false,
            props.counties,
            'Surface', 
            (surfaceTract: SurfaceTract) => {
                addSurfaceTract(push, surfaceTract);
            },
            null
        );
    };

    const containerHeader = (push: (value: AnyObject) => void) => {
        return (
            <>
                <fieldset style={{ padding: '.3rem 0', borderWidth: '1px 0 0 0' }}>
                    <legend className="legend">Surface Ownership Information</legend>
                </fieldset>
                <span style={warningStyle}>ALL INTEREST REPRESENTED AS DECIMAL</span>
                <div style={{ textAlign: 'right', marginRight: '1rem' }}>
                    <Button
                        text={`Add Surface ${props.landSystem === LandSystem.Eastern ? 'Parcel' : 'Tract'}`}
                        size="medium"
                        intent="primary"
                        onClick={() => createSurfaceTract(push)}
                        disabled={props.isFormDirty}
                    />
                </div>
            </>
        );
    };

    // tslint:disable-next-line:max-line-length
    const ownershipContactCategoryItems =
        props.ownershipContactCategories && props.ownershipContactCategories.length > 0
            ? props.ownershipContactCategories.map((type: OwnershipContactCategory) => new SelectItem(type.id, type.name))
            : undefined;

    return (
        <Form
            onSubmit={onSubmit}
            initialValues={{ surfaceTracts: surfaceTracts }}
            mutators={{ ...arrayMutators }}
            render={({ handleSubmit, form, values }) => {
                formApi = form;
                submit = handleSubmit;

                return (
                    <>
                        <FieldArray name="surfaceTracts">
                            {({ fields }) => {
                                if (fields.length === 0) {
                                    return (
                                        <>
                                            {containerHeader(fields.push)}
                                            <div
                                                style={{
                                                    margin: '1rem',
                                                    padding: '1rem',
                                                    height: '4rem',
                                                    fontWeight: 'bold',
                                                    border: '2px solid red',
                                                    fontSize: '1.1em',
                                                    textAlign: 'center',
                                                }}
                                            >
                                                No Surface Ownerships found
                                            </div>
                                        </>
                                    );
                                }

                                if (loading) {
                                    return (
                                        <>
                                            {containerHeader(fields.push)}
                                            <div
                                                style={{
                                                    margin: '1rem',
                                                    padding: '1rem',
                                                    height: '4rem',
                                                    fontWeight: 'bold',
                                                    border: '2px solid #ccc',
                                                    fontSize: '1.1em',
                                                    textAlign: 'center',
                                                }}
                                            >
                                                Loading ...
                                            </div>
                                        </>
                                    );
                                }

                                return (
                                    <>
                                        {containerHeader(fields.push)}
                                        <CardContainer>
                                            {fields.map((name: string, index: number) => (
                                                <SurfaceTractCard
                                                    key={name}
                                                    formApi={formApi}
                                                    id={fields.value[index].id}
                                                    number={fields.value[index].number}
                                                    surfaceTractAcreage={fields.value[index].acres}
                                                    onSurfaceTractDeleted={() => fields.remove(index)}
                                                    landSystem={props.landSystem}
                                                    namePrefix={`${name}`}
                                                    contactCategories={ownershipContactCategoryItems}
                                                    openContactNotes={props.openContactNotes}
                                                    updateTractPage={props.updateTractPage}
                                                    leaseBuyers={leaseBuyerList}
                                                />
                                            ))}
                                        </CardContainer>
                                    </>
                                );
                            }}
                        </FieldArray>
                    </>
                );
            }}
        />
    );
});

export default SurfaceOwnershipList;



// WEBPACK FOOTER //
// ./src/components/project/sections/tract/components/SurfaceOwnershipList.tsx